import React from "react";
import { Phone, CalendarCheck, ShieldCheck, Award, HandCoins, Star } from "lucide-react";
import { BUSINESS } from "@/lib/business";

const BADGES = [
  { icon: Award, label: "ASE Certified" },
  { icon: ShieldCheck, label: "Veteran Owned" },
  { icon: HandCoins, label: "Financing Available" },
  { icon: Star, label: "Military Discounts" },
];

const HIGHLIGHTS = [
  "Veteran Owned",
  "Honest Pricing",
  "Same-Day Repairs Available",
  "Trusted Local Reputation",
];

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[88vh] md:min-h-[92vh] w-full overflow-hidden bg-[#0B132B]"
    >
      {/* Background image */}
      <img
        src="https://images.unsplash.com/photo-1771340012378-3c86cb649193?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
        alt="ASE certified mechanic working on a modern car engine"
        className="absolute inset-0 w-full h-full object-cover object-center"
        loading="eager"
      />
      {/* Gradient overlay */}
      <div className="absolute inset-0 hero-overlay" />
      {/* Red vertical accent */}
      <div className="absolute left-0 top-0 bottom-0 w-1 md:w-1.5 bg-[#D32F2F] z-[2]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 md:pt-36 pb-16 md:pb-24">
        <div className="max-w-3xl">
          {/* Label */}
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#D32F2F]" />
            <span className="font-mono-label text-[#D32F2F]">
              Madison Heights · MI · Veteran-Owned
            </span>
          </div>

          {/* Headline */}
          <h1
            data-testid="hero-headline"
            className="font-display text-white text-4xl sm:text-5xl lg:text-[4.2rem] leading-[1.02] tracking-tight font-bold"
          >
            Honest Auto Repair{" "}
            <span className="text-[#D32F2F]">Backed by Integrity,</span>{" "}
            Precision, and Service.
          </h1>

          {/* Subhead */}
          <p
            data-testid="hero-subhead"
            className="mt-6 text-white/85 text-lg md:text-xl leading-relaxed max-w-2xl"
          >
            Veteran-owned and trusted by Madison Heights drivers for fast, fair,
            and reliable automotive repair you can count on.
          </p>

          {/* Highlights */}
          <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
            {HIGHLIGHTS.map((h) => (
              <li
                key={h}
                data-testid={`hero-highlight-${h.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center gap-2 text-white/90 text-sm font-medium"
              >
                <span className="w-1.5 h-1.5 bg-[#D32F2F]" />
                {h}
              </li>
            ))}
          </ul>

          {/* CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a
              href="#contact"
              data-testid="hero-book-appointment-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-semibold py-4 px-8 rounded-none transition-colors"
            >
              <CalendarCheck className="w-5 h-5" strokeWidth={2.5} />
              Book Your Appointment
            </a>
            <a
              href={`tel:${BUSINESS.phoneDial}`}
              data-testid="hero-call-now-btn"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0B132B] font-semibold py-4 px-8 rounded-none transition-colors"
            >
              <Phone className="w-5 h-5" strokeWidth={2.5} />
              Call {BUSINESS.phoneDisplay}
            </a>
          </div>

          {/* Trust badges */}
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-0 border border-white/15 bg-white/[0.04] backdrop-blur-sm">
            {BADGES.map(({ icon: Icon, label }, i) => (
              <div
                key={label}
                data-testid={`hero-badge-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 p-4 ${
                  i < BADGES.length - 1 ? "sm:border-r border-white/15" : ""
                } ${i < 2 ? "border-b sm:border-b-0 border-white/15" : ""}`}
              >
                <Icon className="w-5 h-5 text-[#D32F2F] shrink-0" strokeWidth={2.5} />
                <span className="text-white text-xs sm:text-sm font-semibold tracking-wide">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom info strip */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#0B132B] border-t border-white/10 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center gap-x-8 gap-y-1 text-xs md:text-sm text-white/80">
          <span className="font-mono-label text-[#D32F2F]">Now Serving</span>
          <span>{BUSINESS.address1}, {BUSINESS.city}, {BUSINESS.state} {BUSINESS.zip}</span>
          <span className="hidden md:inline text-white/40">·</span>
          <span>{BUSINESS.hoursShort}</span>
        </div>
      </div>
    </section>
  );
}
